import { EditOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Checkbox, Col, Collapse, Form, Input, Radio, Row, Select, message, Upload, Modal } from 'antd';
import { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import AutoOpenSelect from '../../SelectComponent/AutoOpenSelect';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../../Images/Back.svg';
import { fetchAlloysList, resetAlloyList } from '../../../slices/alloy';
import { fetchTempersList, resetCurrentTemperPage, resetTempersList } from '../../../slices/tempers';
import { fetchjobWorkList, resetJobWorkList } from '../../../slices/job-work';
import { fetchInquiryById, deleteInquiryDetailById, updateInquiry } from '../../../slices/inquiry';

const { Option } = Select;
const { Panel } = Collapse;

function EditInquiry() {
  const [form] = Form.useForm();
  let navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [activePanels, setActivePanels] = useState(['1', '2', '3', '4', '5']);
  const { loading } = useSelector(state => state.inquiry);
  const { list: alloyList, loading: alloyLoading } = useSelector(state => state.alloy);
  const { list: temperList, loading: temperLoading } = useSelector(state => state.temper);
  const { jobWorkdata, loading: jobWorkLoading } = useSelector(state => state.jobWork);
  const [inquirySouceAttachmentList, setInquirySouceAttachmentList] = useState([]);

  useEffect(() => {
    dispatch(resetAlloyList());
    dispatch(resetTempersList());
    dispatch(resetCurrentTemperPage());
    dispatch(resetJobWorkList());
    dispatch(fetchAlloysList({ page: 1, pagesize: 1000 }));
    dispatch(fetchTempersList({ page: 1, pagesize: 1000 }))
    dispatch(fetchjobWorkList());
  }, [dispatch]);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  useEffect(() => {
    const fetchInquiry = async () => {
      try {
        dispatch(fetchInquiryById(id))
          .unwrap()
          .then(response => {
            if (response) {
              const inquiryData = response?.data;
              if (inquiryData) {
                // 🔹 Map drawing attachments into upload-like structure
                const inquiryDetails = inquiryData.inquiry_details.map((detail) => ({
                  id: detail.id || null,
                  section_no: detail.section_no,
                  description: detail.description,
                  standard_confirmation: detail.standard_confirmation,
                  standard_confirmation_other: detail.standard_confirmation_other || null,
                  alloy: detail.alloy,
                  temper: detail.temper,
                  length: detail.length || '',
                  tolerance_plus: detail?.tolerance_plus || null ,
                  tolerance_minus: detail?.tolerance_minus || null,
                  post_operation: detail.post_operation || null,
                  post_operation_other: detail?.post_operation_other,
                  end_application: detail.end_application,
                  surface_finish: detail.surface_finish,
                  anodising: detail?.anodising,
                  anodising_description: detail?.anodising_description,
                  powder_coating: detail?.powder_coating,
                  powder_coating_description: detail?.powder_coating_description,
                  pvdf: detail?.pvdf,
                  pvdf_description: detail?.pvdf_description,
                  cutting: detail?.cutting,
                  machining: detail?.machining,
                  deburring: detail?.deburring,
                  drawing_file: detail.drawing_attachment
                    ? [
                      {
                        uid: '-1',
                        name: detail.drawing_attachment.split("/").pop(),
                        url: detail.drawing_attachment,
                        status: "done",
                      },
                    ]
                    : [],
                }));

                // 🔹 Map source attachment
                const sourceFiles = inquiryData.source_attachment
                  ? [
                    {
                      uid: '-1', // Use a negative number for existing files
                      name: inquiryData.source_attachment.split('/').pop(),
                      url: inquiryData.source_attachment,
                      status: 'done',
                      thumbUrl: inquiryData.source_attachment,
                    },
                  ]
                  : [];
                setInquirySouceAttachmentList(sourceFiles);


                // 🔹 Set initial form values
                const formValues = {
                  customer_name: inquiryData.customer_name,
                  contact_persons: inquiryData.contact_persons,
                  inquiry_details: inquiryDetails,
                  initial_requirement: inquiryData.initial_requirement,
                  annual_requirement: inquiryData.annual_requirement,
                  inquiry_source: inquiryData.inquiry_source,
                  source_of_inquiry_detail: sourceFiles,
                  special_notes: inquiryData.special_notes,
                  certifications_required: inquiryData.certifications_required,
                  packaging_requirements: inquiryData.packaging_requirements,
                  additional_notes: inquiryData.additional_notes,
                };

                form.setFieldsValue(formValues);
              }
            }
          })
          .catch(error => {
            console.error(error);
          });
      } catch (error) {
        console.error('');
      }
    };

    fetchInquiry();
  }, [id, form, dispatch]);

  const handleFinish = values => {


    const cleanedInquiryDetails = values.inquiry_details.map(item => {
      const newItem = { ...item };

      // Keep only checked Engineering options and their prices
      ["cutting", "machining", "deburring"].forEach(opt => {
        if (!newItem[opt]) {
          delete newItem[opt];
        }
      });

      ["anodising", "powder_coating", "pvdf"].forEach(opt => {
        if (!newItem[opt]) {
          delete newItem[opt];
          delete newItem[`${opt}_description`];
        }
      });

      return newItem;
    });
    values.inquiry_details = cleanedInquiryDetails;
    dispatch(updateInquiry({ id: id, values: values, inquirySouceAttachmentList }))
      .unwrap()
      .then(result => {
        if (result.success) {
          message.success('Inquiry Updated Successfully.');
          navigate('/inquiry-list');
        }
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }

          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error);
        } else if (error instanceof Error) {
          message.error(error.message);
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const onFinishFailed = errorInfo => {
    const errorFields = errorInfo.errorFields.map(field => field.name[0]);

    const panelMap = {
      customer_name: '1',
      inquiry_contact_persons: '2',
      inquiry_details: '3',
      initial_requirement: '4',
      annual_requirement: '4',
    };

    const panelsToOpen = [
      ...new Set(errorFields.map(field => panelMap[field]).filter(Boolean)),
    ];

    setActivePanels(panelsToOpen);
  };

  return (
    <>
      <div className="sticyaction">
        <div className="pagenamerow">
          <h2>Edit Inquiry</h2>
          <div className="bredcrumbwrp">
            <Link to="/inquiry-list" className="back">
              <Back /> BACK
            </Link>
            <Breadcrumb
              items={[
                { title: <Link to="/dashboard">Home</Link> },
                { title: <Link to="/inquiry-list">Inquiry</Link> },
                { title: 'Edit Inquiry' },
              ]}
            />
          </div>
        </div>
      </div>
      <div className="formwrapper">
        <Form
          layout="vertical"
          form={form}
          onFinish={handleFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Collapse activeKey={activePanels} onChange={setActivePanels} className="mb-4">
            <Panel header="Customer Details" key="1" forceRender={true}>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item label="Customer Name" name="customer_name" rules={[
                    { required: true, message: 'Please enter Customer name' },
                    { pattern: /^[A-Za-z\s]+$/, message: "Customer Name can only contain letters and spaces" },
                  ]}>
                    <Input placeholder="Enter Customer Name" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
          <Collapse activeKey={activePanels} onChange={setActivePanels} className="mb-4">
            <Panel header="Contact Persons" key="2" forceRender={true}>
              <Form.List name="contact_persons">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <Row
                        className="rowItem mx-0"
                        gutter={[24, 24]}
                        align="middle"
                        key={key}
                        wrap={false}
                      >
                        <Col flex="auto">
                          <Row gutter={16}>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'name']}
                                label="Name"
                                rules={[
                                  { required: true, message: 'Please enter valid name' },
                                  { pattern: /^[A-Za-z\s]+$/, message: "Name can only contain letters and spaces" },
                                ]}
                              >
                                <Input
                                  placeholder="Enter Name"
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'designation']}
                                label="Designation"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please enter valid designation',
                                  },
                                  {
                                    pattern: /^[a-zA-Z\s]+$/,
                                    message: 'Please enter valid designation',
                                  },
                                ]}
                              >
                                <Input
                                  placeholder="Enter Designation"
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6}>
                              <Form.Item
                                {...restField}
                                name={[name, 'email']}
                                label="Email"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Email is required',
                                  },
                                  {
                                    type: 'email',
                                    message: 'Please enter a valid email address',
                                  },
                                ]}
                              >
                                <Input placeholder="Enter Email" />
                              </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                              <Form.Item
                                {...restField}
                                name={[name, 'mobile']}
                                label="Mobile Number"
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please input your phone number!',
                                  },
                                ]}
                              >
                                <PhoneInput
                                  country="in"
                                  enableSearch
                                  placeholder="Enter phone number"
                                />
                              </Form.Item>
                            </Col>

                            
                          </Row>
                        </Col>

                        <div className="addwrp">
                          {index !== 0 && (
                            <Button
                              type="primary"
                              icon={<MinusCircleOutlined />}
                              onClick={() => remove(name)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginTop: '18px',
                                marginLeft: '-29px',
                              }}
                            />
                          )}
                        </div>
                      </Row>
                    ))}
                     <Form.Item>
                                <div className="w-100 flex justify-end">
                                  <Button
                                    type="primary"
                                    onClick={() => {
                                      if (fields.length < 15) {
                                        add();
                                      } else {
                                        message.error(
                                          'You can only add up to 15 records.',
                                        );
                                      }
                                    }}
                                    icon={<PlusOutlined />}
                                  >
                                    Add Contact Person
                                  </Button>
                                </div>
                              </Form.Item>
                  </>
                )}
              </Form.List>
            </Panel>
          </Collapse>
          <Collapse activeKey={activePanels} onChange={setActivePanels} className="mb-4">
            <Panel header="Technical Details" key="3" forceRender={true}>
              <Form.List name="inquiry_details">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <>
                        <Row
                          gutter={[24, 24]}
                          className="rowItem mx-0"
                          align="middle"
                          key={key}
                          wrap={false}
                          style={{ width: '100%' }}
                        >
                          <Col flex="auto">
                            <Row gutter={[16]} key={key}>
                              <Col xs={12} md={8} xl={6} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'section_no']}
                                  label="Section / Drawing / Part No."
                                  rules={[
                                    { required: true, message: 'Please enter section' },
                                    { pattern: /^[a-zA-Z0-9-/]+$/, message: "Only letters, numbers, hyphen (-) and slash (/) are allowed" },
                                  ]}
                                >
                                  <Input placeholder="Enter section/drawing/part no" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8} xl={6} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'description']}
                                  label="Description"
                                  rules={[
                                    {
                                      required: false,
                                      message: 'Please enter description ',
                                    },
                                  ]}
                                >
                                  <Input placeholder="Enter description" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={4} xl={6} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  label="Drawing File"
                                  name={[name, 'drawing_file']}
                                  valuePropName="fileList"
                                  getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please upload a drawing file',
                                    },
                                    {
                                      validator: (_, fileList) => {
                                        if (!fileList) return Promise.resolve();
                                        const allowedExtensions = /\.(pdf|jpe?g|png|dwg|dxf|step|stp|igs|iges|stl|sat|x_t|x_b|prt|sldprt|slddrw|ipt|dwf|dwfx)$/i;
                                        for (let file of fileList) {
                                          const fileName = file?.originFileObj?.name || file.name;
                                          if (!allowedExtensions.test(fileName)) {
                                            return Promise.reject(
                                              new Error("Invalid file type. Allowed: PDF, Images, CAD files (DWG, DXF, STEP, IGES, STL, etc.)")
                                            );
                                          }
                                        }
                                        return Promise.resolve();
                                      },
                                    },
                                  ]}
                                >
                                  <Upload
                                    beforeUpload={() => false}
                                    listType="text"
                                    maxCount={1}
                                    accept=".pdf,.jpeg,.jpg,.png,.dwg,.dxf,.step,.stp,.igs,.iges,.stl,.sat,.x_t,.x_b,.prt,.sldprt,.slddrw,.ipt,.dwf,.dwfx"
                                    onChange={(info) => {
                                      const file = info.fileList[info.fileList.length - 1];
                                      const allowedExtensions = /\.(pdf|jpe?g|png|dwg|dxf|step|stp|igs|iges|stl|sat|x_t|x_b|prt|sldprt|slddrw|ipt|dwf|dwfx)$/i;

                                      if (file) {
                                        const fileName = file?.originFileObj?.name || file.name;
                                        if (!allowedExtensions.test(fileName)) {
                                          message.error("Invalid file type. Allowed: PDF, Images, CAD files (DWG, DXF, STEP, IGES, STL, etc.)");
                                          return;
                                        }
                                      }

                                      const fieldName = ['inquiry_details', name, 'drawing_file'];
                                      form.setFieldValue(fieldName, info.fileList);
                                      form.validateFields([fieldName]);
                                    }}
                                  >
                                    <Button icon={<UploadOutlined />}>Upload</Button>
                                    <label className="filenote">
                                      Upload PDF, Images, CAD files (DWG, DXF, STEP, IGES, STL, etc. file
                                    </label>
                                  </Upload>
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8} xl={4} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'standard_confirmation']}
                                  label="Standard Confirmation"
                                  rules={[
                                    {
                                      required: false,
                                      message: 'Please select Standard Confirmation',
                                    },
                                  ]}
                                >
                                  <Select placeholder="Select Standard Confirmation">
                                    <Select.Option value="EN">EN</Select.Option>
                                    <Select.Option value="IS">IS</Select.Option>
                                    <Select.Option value="ASTM">ASTM</Select.Option>
                                    <Select.Option value="JIS">JIS</Select.Option>
                                    <Select.Option value="Others">Others</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Form.Item
                                shouldUpdate={(prevValues, currentValues) =>
                                  prevValues.inquiry_details?.[index]?.standard_confirmation !==
                                  currentValues.inquiry_details?.[index]?.standard_confirmation
                                }
                                noStyle
                              >
                                {({ getFieldValue }) =>
                                  getFieldValue(["inquiry_details", index, "standard_confirmation"]) === "Others" ? (
                                    <Col xs={12} md={8} xl={6}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "standard_confirmation_other"]}
                                        label="Please Specify Other Standard Confirmation"
                                        rules={[
                                          { required: true, message: "Please specify other standard confirmation" },
                                        ]}
                                      >
                                        <Input placeholder="Enter other standard confirmation" />
                                      </Form.Item>
                                    </Col>
                                  ) : null
                                }
                              </Form.Item>
                              <Col xs={12} md={8} xl={4} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'alloy']}
                                  label="Alloy"
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please select alloy',
                                    },
                                  ]}
                                >
                                  <Select placeholder="Select Alloy" loading={alloyLoading} disabled={alloyLoading} showSearch optionFilterProp="children">
                                    {alloyList.map((item, index) => (
                                      <Select.Option key={index} value={item.id}>
                                        {item.name} - {item.color_code}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8} xl={6} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'temper']}
                                  label="Temper"
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please select temper',
                                    },
                                  ]}
                                >
                                  <Select placeholder="Select Temper" loading={temperLoading} disabled={temperLoading} showSearch optionFilterProp="children">
                                    {temperList.map((item, index) => (
                                      <Select.Option key={index} value={item.id}>
                                        {item.name} - {item.code}
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Col>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'length']}
                                  label="Cut Length (mm)"
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please enter cut length',
                                    },
                                    {
                                      pattern: /^\d{3,4}$/,
                                      message: "Please enter a 3 or 4 digit number",
                                    },
                                  ]}
                                >
                                  <Input placeholder="0" />
                                </Form.Item>
                              </Col>
                               <Col xs={12} md={8} xl={4} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'tolerance_minus']}
                                  label={`Tolerance (mm) -`}
                                  rules={[
                                    {
                                      required: false,
                                      message: 'Please enter minus tolerance',
                                    },
                                    {
                                      pattern: /^[+-]?\d+(\.\d+)?$/,
                                      message: "Please enter a valid number, optionally with + or -",
                                    },
                                  ]}
                                >
                                  <Input placeholder="0" />
                                </Form.Item>
                              </Col>
                              <Col>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'tolerance_plus']}
                                  label="Tolernace (mm) +"
                                  rules={[
                                    {
                                      required: false,
                                      message: 'Please enter plus tolerance',
                                    },
                                    {
                                      pattern: /^[+-]?\d+(\.\d+)?$/,
                                      message: "Please enter a valid number, optionally with + or -",
                                    },
                                  ]}
                                >
                                  <Input placeholder="0" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8} xl={4} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'post_operation']}
                                  label="Post Operation"
                                  rules={[
                                    {
                                      required: false,
                                      message: 'Please select post operation',
                                    },
                                  ]}
                                >
                                  <Select placeholder="Select Post Operation"
                                    onChange={(value) => {
                                      if (value !== "Others") {
                                        // Safely remove the "Others" field from array
                                        const details = form.getFieldValue("inquiry_details") || [];
                                        if (details[index]?.post_operation_custom) {
                                          const newDetails = [...details];
                                          delete newDetails[index].post_operation_custom;
                                          form.setFieldsValue({ inquiry_details: newDetails });
                                        }
                                      }
                                    }}
                                  >
                                    <Select.Option value="Banding">Banding</Select.Option>
                                    <Select.Option value="Drilling">Drilling</Select.Option>
                                    <Select.Option value="Punching">Punching</Select.Option>
                                    <Select.Option value="Machining">Machining</Select.Option>
                                    <Select.Option value="Tapping">Tapping</Select.Option>
                                    <Select.Option value="Others">Others</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>
                              <Form.Item
                                shouldUpdate={(prevValues, currentValues) =>
                                  prevValues.inquiry_details?.[index]?.post_operation !==
                                  currentValues.inquiry_details?.[index]?.post_operation
                                }
                                noStyle
                              >
                                {({ getFieldValue }) =>
                                  getFieldValue(["inquiry_details", index, "post_operation"]) === "Others" ? (
                                    <Col xs={12} md={8} xl={6}>
                                      <Form.Item
                                        {...restField}
                                        name={[name, "post_operation_other"]}
                                        label="Please Specify Other Operation"
                                        rules={[
                                          { required: true, message: "Please specify other post operation" },
                                        ]}
                                      >
                                        <Input placeholder="Enter other post operation" />
                                      </Form.Item>
                                    </Col>
                                  ) : null
                                }
                              </Form.Item>
                              <Col xs={12} md={8} xl={6} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'end_application']}
                                  label="End Application"
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Please enter end application ',
                                    },
                                  ]}
                                >
                                  <Input placeholder="Enter end application" />
                                </Form.Item>
                              </Col>
                              <Col xs={12} md={8} xl={6} xxl={4}>
                                <Form.Item
                                  {...restField}
                                  label="Surface Finish"
                                  name={[name, 'surface_finish']}
                                  rules={[
                                    {
                                      required: false,
                                      message: 'Please select Surface Finish',
                                    },
                                  ]}
                                >
                                  <AutoOpenSelect
                                    placeholder="Select Surface Finish"
                                    mode="multiple"
                                    value={form.getFieldValue([
                                      'inquiry_details',
                                      name,
                                      'surface_finish',
                                    ])}
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                    loading={jobWorkLoading}
                                    disabled={jobWorkLoading}
                                    notFoundContent={jobWorkLoading ? "Loading surface finishes..." : "No options found"}
                                    onChange={(value) => {
                                      const selectedItems = jobWorkdata?.filter(item => value.includes(item.id));
                                      const selectedNames = selectedItems.map(item => item.name);

                                      const engineeringId = jobWorkdata?.find(item => item.name === "Engineering")?.id;
                                      const surfaceTreatmentId = jobWorkdata?.find(item => item.name === "Surface treatment")?.id;

                                      const details = form.getFieldValue("inquiry_details") || [];
                                      if (!details[index]) return;
                                      const newDetails = [...details];

                                     
                                      // Delete Engineering-related fields if deselected
                                      if (!value.includes(engineeringId)) {
                                        ["cutting", "machining", "deburring"].forEach(opt => {
                                          if (newDetails[index][opt] !== undefined) delete newDetails[index][opt]; // remove boolean
                                          //if (newDetails[index][`${opt}_price`] !== undefined) delete newDetails[index][`${opt}_price`]; // remove price
                                        });
                                      }

                                      // Delete Surface Treatment -related fields if deselected
                                      if (!value.includes(surfaceTreatmentId)) {
                                        ["anodising", "powder_coating", "pvdf"].forEach(opt => {
                                          if (newDetails[index][opt] !== undefined) delete newDetails[index][opt]; // remove boolean
                                          if (newDetails[index][`${opt}_description`] !== undefined) delete newDetails[index][`${opt}_description`]; // remove description
                                          //if (newDetails[index][`${opt}_price`] !== undefined) delete newDetails[index][`${opt}_price`]; // remove price
                                        });
                                      }

                                      form.setFieldsValue({ inquiry_details: newDetails });
                                    }}
                                    dropdownRender={menu => <>{menu}</>}
                                  >
                                    {jobWorkdata?.map(item => (
                                      <Option key={item.id} value={item.id}>
                                        {item.name}
                                      </Option>
                                    ))}
                                  </AutoOpenSelect>
                                </Form.Item>
                              </Col>

                              <Form.Item
                                shouldUpdate={(prevValues, currentValues) =>
                                  prevValues.inquiry_details?.[index]?.surface_finish !==
                                  currentValues.inquiry_details?.[index]?.surface_finish
                                }
                                noStyle
                              >
                                {({ getFieldValue }) => {
                                  const selectedSurfaceFinish = getFieldValue(["inquiry_details", index, "surface_finish"]) || [];

                                  const selectedItems = jobWorkdata?.filter(item => selectedSurfaceFinish.includes(item.id));

                                  const selectedNames = selectedItems.map(item => item.name);

                                  // Example: show conditional field if Laser Marking selected
                                  const hasEngineering = selectedItems.some(item => item.name === "Engineering");
                                  const hasSurfaceTreatment = selectedItems.some(item => item.name === "Surface treatment");
                                  
                                  return (
                                    <>
                                      {/* --- Engineering Fields --- */}
                                      {hasEngineering && (
                                        <>
                                          {/* Checkboxes */}
                                           <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                                <Form.Item
                                                  name={[name, "cutting"]}
                                                  valuePropName="checked"
                                                  noStyle
                                                >
                                                  <Checkbox>Cutting</Checkbox>
                                                </Form.Item>
                                             
                                                <Form.Item
                                                  name={[name, "machining"]}
                                                  valuePropName="checked"
                                                  noStyle
                                                >
                                                  <Checkbox>Machining</Checkbox>
                                                </Form.Item>
                                        
                                                <Form.Item
                                                  name={[name, "deburring"]}
                                                  valuePropName="checked"
                                                  noStyle
                                                >
                                                  <Checkbox>Deburring</Checkbox>
                                                </Form.Item>
                                         
                                            <Form.Item
                                              shouldUpdate
                                              noStyle
                                              rules={[
                                                {
                                                  validator: () => {
                                                    const cutting = form.getFieldValue(["inquiry_details", index, "cutting"]);
                                                    const machining = form.getFieldValue(["inquiry_details", index, "machining"]);
                                                    const deburring = form.getFieldValue(["inquiry_details", index, "deburring"]);

                                                    if (cutting || machining || deburring) {
                                                      return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error("Select at least one engineering option"));
                                                  },
                                                },
                                              ]}
                                            >
                                              {() => (
                                                <Col style={{ color: 'red', marginTop: 4 }}>
                                                  {/* Display error manually */}
                                                  {!(form.getFieldValue(["inquiry_details", index, "cutting"]) ||
                                                    form.getFieldValue(["inquiry_details", index, "machining"]) ||
                                                    form.getFieldValue(["inquiry_details", index, "deburring"]))
                                                    ? "Select at least one engineering option"
                                                    : null}
                                                </Col>
                                              )}
                                            </Form.Item>
                                          </Col>

                                        
                                        </>
                                      )}
                                      {/* --- Surface Treatment Fields --- */}
                                      {hasSurfaceTreatment && (
                                        <>
                                          {/* Checkboxes */}
                                          <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                           
                                                <Form.Item
                                                  name={[name, "anodising"]}
                                                  valuePropName="checked"
                                                  noStyle
                                                >
                                                  <Checkbox>Anodising</Checkbox>
                                                </Form.Item>
                                           
                                                <Form.Item
                                                  name={[name, "powder_coating"]}
                                                  valuePropName="checked"
                                                  noStyle
                                                >
                                                  <Checkbox>Powder Coating</Checkbox>
                                                </Form.Item>
                                             
                                                <Form.Item
                                                  name={[name, "pvdf"]}
                                                  valuePropName="checked"
                                                  noStyle
                                                >
                                                  <Checkbox>PVDF</Checkbox>
                                                </Form.Item>
                                              
                                            <Form.Item
                                              shouldUpdate
                                              noStyle
                                              rules={[
                                                {
                                                  validator: () => {
                                                    const anodising = form.getFieldValue(["inquiry_details", index, "anodising"]);
                                                    const powder_coating = form.getFieldValue(["inquiry_details", index, "powder_coating"]);
                                                    const pvdf = form.getFieldValue(["inquiry_details", index, "pvdf"]);

                                                    if (anodising || powder_coating || pvdf) {
                                                      return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error("Select at least one option"));
                                                  },
                                                },
                                              ]}
                                            >
                                              {() => (
                                                <Col style={{ color: 'red', marginTop: 4 }}>
                                                  {!(form.getFieldValue(["inquiry_details", index, "anodising"]) ||
                                                    form.getFieldValue(["inquiry_details", index, "powder_coating"]) ||
                                                    form.getFieldValue(["inquiry_details", index, "pvdf"]))
                                                    ? "Select at least one surface treatment option"
                                                    : null}
                                                </Col>
                                              )}
                                            </Form.Item>
                                          </Col>

                                          {/* Nested conditional price fields */}
                                          <Form.Item
                                            noStyle
                                            shouldUpdate={(prev, curr) =>
                                              prev.inquiry_details?.[index]?.anodising !== curr.inquiry_details?.[index]?.anodising ||
                                              prev.inquiry_details?.[index]?.powder_coating !== curr.inquiry_details?.[index]?.powder_coating ||
                                              prev.inquiry_details?.[index]?.pvdf !== curr.inquiry_details?.[index]?.pvdf
                                            }
                                          >
                                            {({ getFieldValue }) => {
                                              const anodising = getFieldValue(["inquiry_details", index, "anodising"]);
                                              const powder_coating = getFieldValue(["inquiry_details", index, "powder_coating"]);
                                              const pvdf = getFieldValue(["inquiry_details", index, "pvdf"]);
                                              return (
                                                <>
                                                  {anodising && (
                                                   <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                                     
                                                          <Form.Item
                                                            label="Anodising Description"
                                                            name={[name, "anodising_description"]}
                                                            rules={[{ required: true, message: "Enter Anodising Description" }]}
                                                          >
                                                            <Input placeholder="Enter Anodising Description" />
                                                          </Form.Item>
                                                        
                                                    </Col>
                                                  )}

                                                  {powder_coating && (
                                                   <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                                      
                                                          <Form.Item
                                                            label="Powder Coating Description"
                                                            name={[name, "powder_coating_description"]}
                                                            rules={[{ required: true, message: "Enter Powder Coating Description" }]}
                                                          >
                                                            <Input placeholder="Enter Powder Coating Description" />
                                                          </Form.Item>
                                                        
                                                    </Col>
                                                  )}

                                                  {pvdf && (
                                                    <Col xs={12} md={8} xl={6} xxl={6} style={{ marginTop: '24px' }}>
                                                     
                                                          <Form.Item
                                                            label="PVDF Description"
                                                            name={[name, "pvdf_description"]}
                                                            rules={[{ required: true, message: "Enter PVDF Price" }]}
                                                          >
                                                            <Input placeholder="Enter PVDF Description" />
                                                          </Form.Item>
                                                        
                                                    </Col>
                                                  )}
                                                </>
                                              );
                                            }}
                                          </Form.Item>
                                        </>
                                      )}
                                    </>
                                  );
                                }}
                              </Form.Item>
                            </Row>
                          </Col>
                          <Col>
                            <div className="addwrp">
                              {form.getFieldValue("inquiry_details")[index]?.id !== null && (
                                <Button
                                  type="primary"
                                  danger
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => {
                                    const currentDetails = form.getFieldValue("inquiry_details") || [];
                                    const currentItem = currentDetails[index];

                                    if (currentItem?.id) {

                                      Modal.confirm({
                                        title: "Are you sure you want to delete this item?",
                                        content: "This will permanently remove this inquiry detail.",
                                        okText: "Yes, Delete",
                                        cancelText: "Cancel",
                                        okButtonProps: { danger: true },
                                        async onOk() {
                                          try {
                                            message.loading({ content: "Deleting...", key: "deleteDetail" });
                                            await dispatch(deleteInquiryDetailById(currentItem.id)).unwrap();
                                            message.success({ content: "Inquiry detail deleted", key: "deleteDetail" });

                                            // Remove from form after successful deletion
                                            remove(name);
                                          } catch (error) {
                                            message.error({ content: "Failed to delete inquiry detail", key: "deleteDetail" });
                                          }
                                        },
                                      });
                                    } else {
                                      // 🔹 Just remove locally if not saved
                                      remove(name);
                                    }
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                />
                              )}
                            </div>
                          </Col>
                        </Row>
                      </>
                    ))}
                    <Form.Item>
                      <div className="w-100 flex justify-end ">
                        <Button
                          type="primary"
                          onClick={() => {
                            if (fields.length < 500) {

                              add({ surface_finish: [] });
                            } else {
                              message.error('You can only add up to 15 records.');
                            }
                          }}
                          icon={<PlusOutlined />}
                        >
                          Add Item
                        </Button>
                      </div>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </Panel>
          </Collapse>
          <Collapse
            activeKey={activePanels}
            onChange={setActivePanels}
            className="mb-4"
          >
            <Panel header="Quantity Details" key="4" forceRender={true}>
              <Row gutter={16}>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Form.Item
                    label="Initial Requirement (Kgs)"
                    name="initial_requirement"
                    rules={[
                      {
                        pattern: /^\d+(\.\d+)?$/,
                        message: "Please enter a valid number (decimals allowed)",
                      },
                      {
                        validator: (_, value) => {
                          const maxValue = 100000;
                          if (value && parseFloat(value) > maxValue) {
                            return Promise.reject(
                              new Error(`Value cannot exceed ${maxValue} Kgs`)
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter initial requirement" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Form.Item
                    label="Annual Requirement (Kgs)"
                    name="annual_requirement"
                    rules={[
                      {
                        pattern: /^\d+(\.\d+)?$/,
                        message: "Please enter a valid number (decimals allowed)",
                      },
                      {
                        validator: (_, value) => {
                          const maxValue = 100000;
                          if (value && parseFloat(value) > maxValue) {
                            return Promise.reject(
                              new Error(`Value cannot exceed ${maxValue} Kgs`)
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="Enter annual requirement" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Form.Item
                    label="Source of Inquiry"
                    name="inquiry_source"
                  >
                    <Radio.Group>
                      <Radio value="Open_Form">Open Form</Radio>
                      <Radio value="Whatsapp">Whatsapp</Radio>
                      <Radio value="Email">Email</Radio>
                      <Radio value="Call">Call</Radio>
                      <Radio value="Expo">Expo</Radio>
                      <Radio value="Reference">Reference</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6} lg={6}>
                  <Form.Item
                    name="source_of_inquiry_detail"
                    label="Source of Inquiry Attachment"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      if (Array.isArray(e)) {
                        return e;
                      }
                      return e?.fileList;
                    }}
                    rules={[
                      {
                        validator: (_, fileList) => {
                          if (!fileList || fileList.length === 0) return Promise.resolve();

                          const allowedExtensions = /\.(pdf|jpe?g|png)$/i;

                          for (let file of fileList) {
                            // Get filename from various possible sources
                            const fileName = file?.originFileObj?.name || file?.name || '';

                            if (!fileName || !allowedExtensions.test(fileName)) {
                              return Promise.reject(new Error("Only PDF, JPEG, JPG, or PNG files are allowed"));
                            }
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Upload
                      name="file"
                      listType="picture"
                      accept=".pdf,.jpeg,.jpg,.png"
                      maxCount={1}
                      beforeUpload={() => false}
                      showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                      fileList={inquirySouceAttachmentList}
                      onChange={(info) => {
                        const newFileList = info.fileList.map(file => ({
                          uid: file.uid,
                          name: file.name,
                          status: file.status,
                          url: file.url || (file.originFileObj && URL.createObjectURL(file.originFileObj)),
                          originFileObj: file.originFileObj,
                        }));

                        setInquirySouceAttachmentList(newFileList);
                        // The form field will be updated automatically via onChange
                      }}
                      onRemove={() => {
                        // Optional: you can also handle it here directly
                        form.setFieldsValue({ source_of_inquiry_detail: null });
                      }}
                    >
                      <Button icon={<UploadOutlined />}>Upload</Button>
                    </Upload>
                    <label className="filenote">Upload PDF, JPEG, JPG or PNG file</label>
                  </Form.Item>

                </Col>
              </Row>
            </Panel>
          </Collapse>
          <Collapse
            activeKey={activePanels}
            onChange={setActivePanels}
            className="mb-4"
          >
            <Panel
              header="Other Relevant Information"
              key="5"
              forceRender={true}
            >
              <Row gutter={16}>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item label="Special Notes" name="special_notes">
                    <Input placeholder="Enter special notes" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    label="Certifications Required"
                    name="certifications_required"
                  >
                    <Input placeholder="Enter required certifications" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    label="Packaging Requirements"
                    name="packaging_requirements"
                  >
                    <Input placeholder="Enter packaging requirements" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Form.Item
                    label="Additional Notes"
                    name="additional_notes"
                  >
                    <Input placeholder="Enter additional notes" />
                  </Form.Item>
                </Col>
              </Row>
            </Panel>
          </Collapse>
          <div className="flex justify-end">
            <Button type="primary" htmlType="submit" size="large" loading={loading} icon={<EditOutlined />}>
              Update Inquiry
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}

export default EditInquiry;